<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount, nextTick } from "vue";

interface GenicUiStep {
    label: string;
    title: string;
    description: string;
}

defineProps({
    heading: {
        type: String,
        default: "How GenicUI Works",
    },
    subheading: {
        type: String,
        default: "Under The Hood",
    },
    description: {
        type: String,
        default: "",
    },
    steps: {
        type: Array as () => GenicUiStep[],
        default: () => [],
    },
    ctaText: {
        type: String,
        default: "",
    },
    ctaLink: {
        type: String,
        default: "",
    },
});

const emit = defineEmits<{
    (e: "cta-clicked"): void;
}>();

const containerRef = ref<HTMLElement | null>(null);
const mobileProgressReady = ref(false);

function measure() {
    const container = containerRef.value;
    if (!container) return;
    const containerRect = container.getBoundingClientRect();
    const stepEls = Array.from(container.querySelectorAll<HTMLElement>(".genic-step")).slice(0, 3);
    stepEls.forEach((el, i) => {
        container.style.setProperty(`--m-d${i + 1}`, `${el.getBoundingClientRect().top - containerRect.top}px`);
        // Desktop: each dot sits at its card's horizontal center, so measure the
        // card's center x in px and feed the progress-line keyframes exact values.
        const card = el.firstElementChild;
        if (card instanceof HTMLElement) {
            const cardRect = card.getBoundingClientRect();
            container.style.setProperty(
                `--dot-${i + 1}`,
                `${cardRect.left + cardRect.width / 2 - containerRect.left}px`
            );
        }
    });
    mobileProgressReady.value = true;
}

let resizeHandler: (() => void) | undefined;

onMounted(() => {
    nextTick(measure);
    // Re-measure once webfonts are ready so text wrapping (and step heights) is final.
    document.fonts.ready.then(() => nextTick(measure));
    resizeHandler = () => nextTick(measure);
    window.addEventListener("resize", resizeHandler);
});

onBeforeUnmount(() => {
    if (resizeHandler) window.removeEventListener("resize", resizeHandler);
});
</script>

<template>
    <div class="relative">
        <div class="relative z-10">
            <h2 class="text-center">
                <WrGradientText :text="heading"
                    append-class="py-1 bg-[linear-gradient(92.15deg,_var(--text-gradient-1)_-3.57%,_var(--text-gradient-2)_80.72%)]" />
                <span class="text-[#1E1B4B] dark:text-white">{{ subheading }}</span>
            </h2>
            <p class="text-base lg:text-lg text-center text-[#5A5478] dark:text-[#CBCBCB] mt-2">
                {{ description }}
            </p>

            <div ref="containerRef" :class="{ 'genic-progress-ready': mobileProgressReady }"
                class="relative [--step-pad:48px] [--dot-offset:45px] [--dot-size:16px] [--dot-1:18.5%] [--dot-2:48.25%] [--dot-3:78%]
                max-sm:before:absolute max-sm:before:left-[7px] max-sm:before:top-2 max-sm:before:bottom-2 max-sm:before:w-[2px] max-sm:before:rounded-full
                max-sm:before:bg-[linear-gradient(180deg,rgba(71,83,220,0)_0%,#4753DC_28%,#4753DC_72%,rgba(71,83,220,0)_100%)]
                max-sm:before:dark:bg-[linear-gradient(180deg,rgba(255,255,255,0)_0%,#FFFFFF_28%,#FFFFFF_72%,rgba(255,255,255,0)_100%)]
                max-sm:before:content-['']
                max-sm:after:absolute max-sm:after:left-[6px] max-sm:after:top-2 max-sm:after:w-[4px] max-sm:after:rounded-full
                max-sm:after:bg-[linear-gradient(180deg,rgba(71,83,220,0)_0%,#4753DC_100%)]
                max-sm:after:content-['']
                sm:before:absolute sm:before:left-0 sm:before:right-0 sm:before:top-[calc(var(--step-pad)+var(--dot-size)/2)] sm:before:h-px
                sm:before:bg-[linear-gradient(90deg,rgba(71,83,220,0)_0%,#4753DC_52.88%,rgba(71,83,220,0)_100%)]
                sm:before:dark:bg-[linear-gradient(90deg,rgba(255,255,255,0)_0%,#FFFFFF_52.88%,rgba(255,255,255,0)_100%)]
                sm:before:content-['']
                sm:after:absolute sm:after:left-0 sm:after:top-[calc(var(--step-pad)+var(--dot-size)/2-2px)] sm:after:h-[4px] sm:after:w-0
                sm:after:bg-[linear-gradient(270deg,#4753DC_0%,rgba(71,83,220,0)_100%)]
                sm:after:content-['']">
                <div class="grid grid-cols-1 sm:grid-cols-3 gap-10 pt-[var(--step-pad)] max-w-[1014px] mx-auto sm:mr-10 max-sm:pt-0 max-sm:mt-5">
                    <div v-for="(step, index) in steps" :key="index"
                        class="genic-step relative w-full max-w-[265px] mx-auto flex flex-col items-start gap-4 max-sm:gap-2 text-left
                            pt-[var(--dot-offset)] max-sm:mx-0 max-sm:max-w-none max-sm:pl-[36px] max-sm:pt-0">
                        <!-- <div class=""> -->
                        <div
                            class="relative
                            before:absolute before:z-10 before:-top-[var(--dot-offset)] before:left-1/2 before:-translate-x-1/2
                            max-sm:before:top-0 max-sm:before:left-[-36px] max-sm:before:translate-x-0
                            before:size-[var(--dot-size)] before:rounded-full before:bg-[#4753DC]
                            before:shadow-[0px_9px_23.4px_-1px_#4753DC] before:content-[''] p-px rounded-lg bg-[linear-gradient(136.3deg,#E6E1FF_4.31%,#C4B5FD_95.48%)] dark:bg-[linear-gradient(136.3deg,#4650B7_4.31%,#26308D_95.48%)]">
                            <div
                                class="relative overflow-hidden rounded-[7px] bg-[linear-gradient(138.31deg,#FFFFFF_7.92%,#F3F0FF_90.14%)] dark:bg-[linear-gradient(138.31deg,#21276C_7.92%,#080D3D_90.14%)]
                                py-3 px-[26px] after:absolute after:top-10 after:left-1/2 after:-translate-x-1/2
                                after:content-[''] after:bg-[#6970C9] after:blur-[40px] after:size-[40px]">
                                <p
                                    class="text-xl font-semibold text-left text-transparent bg-clip-text bg-[linear-gradient(180deg,#3730A3_0%,#4F46E5_100%)] dark:bg-[linear-gradient(180deg,#FFFFFF_0%,#737CF4_100%)]">
                                    {{ step.label }}
                                </p>
                            </div>
                        </div>
                        <!-- </div> -->
                        <p class="text-[16px] text-[#1E1B4B] dark:text-white tracking-[0.5px]">
                            {{ step.title }}
                        </p>
                        <p class="text-[15px] leading-relaxed text-[#5A5478] dark:text-[#7B84E8]">
                            {{ step.description }}
                        </p>
                    </div>
                </div>
            </div>

            <div v-if="ctaText" class="flex justify-center mt-12">
                <WrButton :link="ctaLink || undefined" @click="$emit('cta-clicked')"
                    container-class="bg-[linear-gradient(95.01deg,#8B9BFF_2.72%,#4753DC_95.35%)]"
                    append-class="text-base font-normal px-6 bg-[linear-gradient(95.99deg,#4753DC_3.69%,#262B7E_96.65%)]">
                    {{ ctaText }}
                </WrButton>
            </div>
        </div>
    </div>
</template>

<style>
/* --dot-1/2/3 are set in px from JS (measure()) at each dot's exact center, so
   the line always lands on the dots; the % values in the markup are only
   pre-measurement fallbacks and never animate (the animation is gated on
   .genic-progress-ready, which is set in the same measure pass). */
@keyframes genic-ui-progress {
    /* left edge -> dot 1 */
    0% {
        left: 0;
        width: 0;
    }

    10% {
        left: 0;
        width: var(--dot-1);
    }

    18% {
        left: 0;
        width: var(--dot-1);
    }

    25% {
        left: var(--dot-1);
        width: 0;
    }

    33% {
        left: var(--dot-1);
        width: 0;
    }

    /* dot 1 -> dot 2 */
    43% {
        left: var(--dot-1);
        width: calc(var(--dot-2) - var(--dot-1));
    }

    51% {
        left: var(--dot-1);
        width: calc(var(--dot-2) - var(--dot-1));
    }

    58% {
        left: var(--dot-2);
        width: 0;
    }

    66% {
        left: var(--dot-2);
        width: 0;
    }

    /* dot 2 -> dot 3 */
    76% {
        left: var(--dot-2);
        width: calc(var(--dot-3) - var(--dot-2));
    }

    84% {
        left: var(--dot-2);
        width: calc(var(--dot-3) - var(--dot-2));
    }

    91% {
        left: var(--dot-3);
        width: 0;
    }

    100% {
        left: var(--dot-3);
        width: 0;
    }
}

/* Mobile: vertical progress line. --m-d1/2/3 are set from JS (each step's top
   offset from the track) because stacked step heights vary on mobile. */
@keyframes genic-ui-progress-m {
    0% {
        top: 8px;
        height: 0;
    }

    10% {
        top: 8px;
        height: var(--m-d1, 0px);
    }

    18% {
        top: 8px;
        height: var(--m-d1, 0px);
    }

    25% {
        top: calc(8px + var(--m-d1, 0px));
        height: 0;
    }

    33% {
        top: calc(8px + var(--m-d1, 0px));
        height: 0;
    }

    43% {
        top: calc(8px + var(--m-d1, 0px));
        height: calc(var(--m-d2, 0px) - var(--m-d1, 0px));
    }

    51% {
        top: calc(8px + var(--m-d1, 0px));
        height: calc(var(--m-d2, 0px) - var(--m-d1, 0px));
    }

    58% {
        top: calc(8px + var(--m-d2, 0px));
        height: 0;
    }

    66% {
        top: calc(8px + var(--m-d2, 0px));
        height: 0;
    }

    76% {
        top: calc(8px + var(--m-d2, 0px));
        height: calc(var(--m-d3, 0px) - var(--m-d2, 0px));
    }

    84% {
        top: calc(8px + var(--m-d2, 0px));
        height: calc(var(--m-d3, 0px) - var(--m-d2, 0px));
    }

    91% {
        top: calc(8px + var(--m-d3, 0px));
        height: 0;
    }

    100% {
        top: calc(8px + var(--m-d3, 0px));
        height: 0;
    }
}

/* Enable the mobile progress animation only after positions are measured, so the
   keyframe starts with the correct --m-d values. Matches Tailwind's max-sm. */
@media not (min-width: 640px) {
    .genic-progress-ready::after {
        animation: genic-ui-progress-m 10s ease-in-out infinite;
    }
}

/* Desktop: same gating, so the line only animates once the --dot px values are
   measured. Matches Tailwind's sm. */
@media (min-width: 640px) {
    .genic-progress-ready::after {
        animation: genic-ui-progress 10s ease-in-out infinite;
    }
}
</style>
